import React, { FC, useMemo } from 'react';
import { FeedbackStudent } from '../../../../config/api/models/Feedback';
import { useMediaQuery, useTheme, styled, Theme } from '@material-ui/core';
import { Icons, ProfilePicture, SvgIcon } from '../../../shared/components';
import { IGroupStudent } from 'src/config/api/models/User';

type TargetAudienceStatusProps = {
  selectedGroupData?: IGroupStudent[];
  feedbackStudents: FeedbackStudent[];
};

type TargetAudienceStatusStudent = {
  student: IGroupStudent;
  isRead: boolean;
};

const Wrapper = styled('div')({
  display: 'flex',
  padding: '1rem 0 2rem 0',
});

const Column = styled('div')<Theme, { numcolumns: number }>(({ theme, numcolumns }) => ({
  width: `${100 / numcolumns}%`,
  padding: `0 0.5rem`,
  '&:first-child': {
    paddingLeft: 0,
  },
  '&:last-child': {
    paddingRight: 0,
  },
}));

const StudentTile = styled('div')<Theme, { odd: 'true' | 'false' }>(({ theme, odd }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '1rem',
  background: odd === 'true' ? theme.palette.grey['50'] : '#fff',
}));

const StudentName = styled('div')({
  flexGrow: 1,
  fontSize: '1.4rem',
  margin: '0 0.5rem',
});

export const TargetAudienceStatus: FC<TargetAudienceStatusProps> = ({
  selectedGroupData,
  feedbackStudents,
}) => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));
  const tablet = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  const numColumns = mobile ? 2 : tablet ? 3 : 4;
  const numPerColumn = Math.ceil(feedbackStudents.length / numColumns);

  const preparedList = useMemo(() => {
    const result: TargetAudienceStatusStudent[] = [];

    feedbackStudents.forEach(feedbackStudent => {
      const studentData = selectedGroupData?.find(
        student => student.id === feedbackStudent.studentId
      );
      if (studentData != null) {
        result.push({
          student: studentData,
          isRead: feedbackStudent.isRead,
        });
      }
    });

    result.sort((a, b) => (a.student.fullName > b.student.fullName ? 1 : -1));

    return result;
  }, [feedbackStudents, selectedGroupData]);

  const list = useMemo(() => {
    const result: TargetAudienceStatusStudent[][] = [];

    let i = 0,
      temp: TargetAudienceStatusStudent[] = [];
    preparedList.forEach(feedbackStudent => {
      if (i === numPerColumn) {
        i = 0;
        result.push(temp);
        temp = [];
      }

      temp.push(feedbackStudent);
      i++;
    });

    result.push(temp);

    return result;
  }, [preparedList, numPerColumn]);

  return list.length > 0 ? (
    <Wrapper>
      {list.map((subList: TargetAudienceStatusStudent[], index) => (
        <Column numcolumns={numColumns} key={index}>
          {subList.map((targetAudienceStudent, index) => (
            <StudentTile odd={index % 2 === 0 ? 'true' : 'false'} key={index}>
              <ProfilePicture initials={targetAudienceStudent.student.initials} size="small" />
              <StudentName>{`${targetAudienceStudent.student.fullName}`}</StudentName>
              {targetAudienceStudent.isRead && (
                <SvgIcon icon={Icons.READ} color={theme.palette.success.main} />
              )}
            </StudentTile>
          ))}
        </Column>
      ))}
    </Wrapper>
  ) : null;
};
